import { CfDataGridRowType, NavigableElement } from '../../../CfDataGridProps';
import { CfDataGridCustomRow } from './CfDataGridCustomRow';
import { CfDataGridRowFooter } from './CfDataGridFooterRow';
import { CfDataGridRow } from './CfDataGridRow';
import { CfDataGridSkeletonRow } from './CfDataGridSkeletonRow';
import { CfDataGridRowByItemIdProps, CfDataGridRowProps, getRowPropsFromRowPropsById } from './common';

export interface RowWrappersByIdProps<TItem, TAppState> {
  rowProps: CfDataGridRowByItemIdProps<TItem, TAppState>;
  rowType: CfDataGridRowType;
}

interface RowWrappersProps<TItem> {
  rowProps: CfDataGridRowProps<TItem>;
  rowType: CfDataGridRowType;
}

export function RowWrappersById<TItem, TAppState>(props: RowWrappersByIdProps<TItem, TAppState>): JSX.Element | null {
  const { rowProps, rowType } = props;

  const itemId = rowProps.itemId ?? '';

  const item: TItem | undefined = rowProps.useSelector((state) => rowProps.itemSelector(itemId, state));
  const previousItem: TItem | undefined = rowProps.useSelector((state) =>
    rowProps.previousItemId ? rowProps.itemSelector(rowProps.previousItemId, state) : undefined
  );

  if (!itemId) {
    return <CfDataGridSkeletonRow tableViewDefinition={rowProps.tableViewDefinition} itemIndex={rowProps.itemIndex} />;
  }

  const normalizedRowProps = getRowPropsFromRowPropsById(rowProps, item, previousItem);
  return <RowWrappers rowProps={normalizedRowProps} rowType={rowType} />;
}

export function RowWrappers<TItem>(props: RowWrappersProps<TItem>): JSX.Element | null {
  const { rowProps, rowType } = props;

  const item = rowProps.item;
  if (!item) {
    return <CfDataGridSkeletonRow tableViewDefinition={rowProps.tableViewDefinition} itemIndex={rowProps.itemIndex} />;
  }

  switch (rowType) {
    case 'row':
      return <CfDataGridRow {...rowProps} />;
    case 'custom-row-after':
      return <CustomRowAfterWrapper {...rowProps} />;
    case 'custom-row-before':
      return <CustomRowBeforeWrapper {...rowProps} />;
    case 'row-footer':
      return <CustomRowFooterWrapper {...rowProps} />;
    case 'row-skeleton':
      return (
        <CfDataGridSkeletonRow tableViewDefinition={rowProps.tableViewDefinition} itemIndex={rowProps.itemIndex} />
      );
    default:
      return null;
  }
}

export function CustomRowBeforeWrapper<TItem>(props: CfDataGridRowProps<TItem>): JSX.Element | null {
  if (!props.item) return null;
  const customRowBefore = props.tableViewDefinition.insertCustomRow?.(
    { item: props.previousItem, itemIndex: props.itemIndex - 1 },
    { item: props.item, itemIndex: props.itemIndex },
    props.recomputeRowHeights,
    props.recomputeRowHeight,
    (navigableElement: NavigableElement, columnIndex?: number) => {
      props.setNavigableElement(navigableElement, Math.min(0, props.itemIndex - 1), columnIndex ?? 0);
    }
  );
  if (!customRowBefore) return null;
  return <CfDataGridCustomRow content={customRowBefore.content} tableRowSxProps={customRowBefore.tableRowSxProps} />;
}

export function CustomRowFooterWrapper<TItem>(props: CfDataGridRowProps<TItem>): JSX.Element | null {
  if (!props.item) return null;
  const footerRow = props.tableViewDefinition.insertFooterRow?.({
    item: props.item,
    itemIndex: props.itemIndex,
    recomputeRowHeights: props.recomputeRowHeights,
    recomputeRowHeight: props.recomputeRowHeight,
    setNavigableElement: (navigableElement: NavigableElement, columnIndex?: number) => {
      props.setNavigableElement(navigableElement, props.itemIndex, columnIndex ?? 0);
    },
  });
  if (!footerRow) return null;
  return <CfDataGridRowFooter content={footerRow.content} tableRowSxProps={footerRow.tableRowSxProps} />;
}

export function CustomRowAfterWrapper<TItem>(props: CfDataGridRowProps<TItem>): JSX.Element | null {
  if (!props.item) return null;
  const customRowAfter =
    props.itemIndex === props.itemCount - 1
      ? props.tableViewDefinition.insertCustomRow?.(
          { item: props.item, itemIndex: props.itemIndex },
          { item: undefined, itemIndex: props.itemCount },
          props.recomputeRowHeights,
          props.recomputeRowHeight,
          (navigableElement: NavigableElement, columnIndex?: number) => {
            props.setNavigableElement(navigableElement, props.itemIndex + 1, columnIndex ?? 0);
          }
        )
      : undefined;
  if (!customRowAfter) return null;
  return <CfDataGridCustomRow content={customRowAfter.content} tableRowSxProps={customRowAfter.tableRowSxProps} />;
}
